import { Env } from '@env';
import {
  CardElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  addDays,
  addHours,
  differenceInHours,
  isAfter,
  isBefore,
  subDays,
  subHours,
} from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import * as Clipboard from 'expo-clipboard';
import { Image } from 'expo-image';
import { useFocusEffect, useNavigation, useRouter } from 'expo-router';
import React, { useEffect, useLayoutEffect } from 'react';
import {
  Dimensions,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Share,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Image as CarImage, Reservation } from '@/api';
import { useCustomer } from '@/api/customers/use-customer';
import type { CreateReservationHoldIntent } from '@/api/reservations/use-create-reservation-hold-intent';
import { useCreateReservationHoldIntent } from '@/api/reservations/use-create-reservation-hold-intent';
import { useReservation } from '@/api/reservations/use-reservation';
import { AdditionalDriversCard } from '@/components/additional-drivers/drivers-card';
import {
  additionalDriverStatus,
  findAdditionalDriver,
  isAdditionalDriverVerified,
} from '@/components/additional-drivers/status';
import ListItemStatus from '@/components/list-item-status';
import { Review } from '@/components/review';
import type { TripStep } from '@/components/trip-timeline';
import TripTimeline, { StepType } from '@/components/trip-timeline';
import {
  formatNumber,
  getCarBrandLogo,
  getCarImages,
  isDesktop,
  openAddressInMaps,
  rgbaToHex,
  snakeToTitleCase,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { usePoll } from '@/core/hooks/use-poll';
import { GREY_40 } from '@/style/colors';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Chip, ChipType } from '@/ui/chip';
import { Dialog } from '@/ui/dialog';
import { Icon } from '@/ui/icon';
import { ArrowRight, Docs, Lock } from '@/ui/icons';
import { ArrowLeft } from '@/ui/icons/arrow-left';
import { Call } from '@/ui/icons/call';
import { Cancel } from '@/ui/icons/cancel';
import { Checkmark } from '@/ui/icons/checkmark';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Dot } from '@/ui/icons/dot';
import { Hourglass } from '@/ui/icons/hourglass';
import { License } from '@/ui/icons/license';
import { Warning } from '@/ui/icons/warning';
import { Loader } from '@/ui/loader';

import BackButton from '../back-button';
import ContactSupport from '../contact-support';
import Footer from '../footer';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';
import { CancelTripModal } from './cancel-trip-modal';
import { SubscriptionCancelTripModal } from './subscription-cancel-trip-modal';

const PAGE_WIDTH = Dimensions.get('window').width;
const PAGE_HEIGHT = Dimensions.get('window').height;

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

const StripeWrapper = ({ id }: { id: number }) => {
  const { theme } = useStyles(stylesheet);
  const router = useRouter();
  const [stripeSecret, setStripeSecret] = React.useState<string | null>(null);
  const [customerSessionSecret, setCustomerSessionSecret] = React.useState<
    string | null
  >(null);

  const {
    mutateAsync: createReservationHoldIntent,
    isPending: isCreatingReservationHoldIntent,
  } = useCreateReservationHoldIntent();

  const {
    data: resData,
    isFetching: isFetchingReservation,
    refetch,
    isFetched,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const needPayment = React.useMemo(() => {
    if (!resData) return false;

    // holdAmount is 0 when no security deposit is taken (EVFMC unlimited subs).
    // Gating on it keeps us from asking Stripe for a $0 hold intent, which
    // would fail and leave this screen stuck rendering nothing.
    return (
      resData.status === 'verified' &&
      resData.holdAmount > 0 &&
      differenceInHours(new Date(resData.startTime), new Date()) < 24
    );
  }, [resData]);

  React.useEffect(() => {
    if (!resData) return;

    if (resData.status === 'cancelled') {
      router.replace('/trips');
      showErrorMessage('This trip has been cancelled');
      return;
    }
  }, [resData]);

  React.useEffect(() => {
    if (resData?.status !== 'completed') return;

    mixpanelService.track('Page Viewed', {
      'Page Name': 'Trip Summary',
    });
  }, [resData?.status]);

  React.useEffect(() => {
    const handleCreateHoldIntent = async () => {
      try {
        const {
          data: {
            stripeCustomerId,
            ephemeralKey,
            stripeSecret,
            customerSessionSecret,
          },
        }: CreateReservationHoldIntent = await createReservationHoldIntent({
          id: id,
        });

        setStripeSecret(stripeSecret);
        setCustomerSessionSecret(customerSessionSecret);
      } catch (error) {
        console.log(error);
      }
    };

    if (!resData) return;

    if (needPayment && !stripeSecret) {
      handleCreateHoldIntent();
    }
  }, [resData, needPayment]);

  if (isFetched && !needPayment) {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          locale: 'en',
          appearance: {
            variables: {
              colorBackground: rgbaToHex(theme.colors.background_2),
            },
            rules: {
              '.Tab': {
                border: 'none',
              },
            },
            theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
          },
        }}
      >
        <TripDetails id={id} />
      </Elements>
    );
  }

  if (!stripePromise || !stripeSecret || !customerSessionSecret || !resData) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: stripeSecret,
        customerSessionClientSecret: customerSessionSecret,
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <TripDetails id={id} stripeSecret={stripeSecret} />
    </Elements>
  );
};

function TripDetails({
  id,
  stripeSecret,
}: {
  id: number;
  stripeSecret?: string;
}) {
  const now = new Date();
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const navigation = useNavigation();
  const [photosModeTab, setPhotosModeTab] = React.useState('pre');
  const [activeImage, setActiveImage] = React.useState<CarImage | null>(null);
  const progress = useSharedValue<number>(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);
  const [carouselActiveIndex, setCarouselActiveIndex] = React.useState(0);
  const stripe = useStripe();
  const elements = useElements();
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >();
  const [isHandlingPaymentPending, setIsHandlingPaymentPending] =
    React.useState(false);
  const [isHandlingPaymentSuccess, setIsHandlingPaymentSuccess] =
    React.useState(false);
  const [isHandlingPaymentError, setIsHandlingPaymentError] =
    React.useState(false);
  // const [permissionResponse, requestPermission] = MediaLibrary.usePermissions();
  const [showCancelTripModal, setShowCancelTripModal] = React.useState(false);
  const [showSubscriptionCancelTripModal, setShowSubscriptionCancelTripModal] =
    React.useState(false);

  const { data: customer } = useCustomer();

  const {
    data: resData,
    isFetching: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: false,
  });

  const isTripConfirmed = (data: Reservation) =>
    ['confirmed', 'rental'].includes(data.status);
  const { poll: pollForTripConfirmed } = usePoll(refetch, isTripConfirmed);

  useLayoutEffect(() => {
    if (!resData) return;

    if (!isDesktop()) {
      navigation.setOptions({
        title: resData.status === 'completed' ? 'Trip Summary' : 'Trip Details',
        headerTitle:
          resData.status === 'completed' ? 'Trip Summary' : 'Trip Details',
        headerLeft: () => (
          <BackButton
            onPress={() => {
              if (activeImage) setActiveImage(null);
              else router.back();
            }}
          />
        ),
      });
    }
  }, [navigation, resData, activeImage, router]);

  const additionalDriver = React.useMemo(() => {
    if (!customer || !resData) return null;

    return findAdditionalDriver(resData?.additionalDrivers, customer);
  }, [resData, customer]);

  const additionalDriverVerified = isAdditionalDriverVerified(additionalDriver);

  useEffect(() => {
    if (!additionalDriver || !resData || additionalDriverVerified) return;

    router.replace(
      additionalDriverStatus(additionalDriver) === 'invite_sent'
        ? `/trips/${resData.id}/accept-invite`
        : `/trips/${resData.id}/additional-drivers/verify`
    );
  }, [additionalDriver, additionalDriverVerified, resData, router]);

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  const handleShare = async () => {
    const link = `${Env.BASE_URL}?ref=${resData.customer.referralCode}`;

    if (Platform.OS === 'web') {
      const copied = await Clipboard.setStringAsync(link);

      if (!copied) {
        showErrorMessage('Issue copying to clipboard');
      } else {
        Toast.show({
          text1: 'Copied to clipboard',
          type: 'success',
        });
      }
    }

    try {
      await Share.share({
        message: link,
      });
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  React.useEffect(() => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: carouselActiveIndex - progress.value,
      animated: false,
    });
  }, [carouselActiveIndex]);

  const tripImagesToShow = React.useMemo(() => {
    if (!activeImage) return [];

    return resData?.preTripCarImages.find(
      (image) => image.photo === activeImage.photo
    )
      ? resData?.preTripCarImages
      : resData?.postTripCarImages;
  }, [activeImage, resData]);

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: false,
    });

    setCarouselActiveIndex(index);
    setActiveImage(tripImagesToShow[index]);
  };

  const onDownloadFile = async (imageUri: string) => {
    // const fileName = imageUri.split('/').pop();
    // let fileUri = FileSystem.documentDirectory! + fileName;
    // try {
    //   const { uri } = await FileSystem.downloadAsync(imageUri, fileUri);
    //   if (permissionResponse.status !== 'granted') {
    //     await requestPermission();
    //   }
    //   await MediaLibrary.saveToLibraryAsync(uri);
    //   showMessage({
    //     message: 'Image saved to photo gallery',
    //     type: 'success',
    //   });
    // } catch (e) {
    //   showErrorMessage(e.message);
    // }
  };

  const timelineSteps = React.useMemo(() => {
    if (!resData) return [];

    const renderDepositStep = (): TripStep => {
      const lessThan24Hours =
        differenceInHours(new Date(resData.startTime), new Date()) < 24;

      let type = '';
      let body = '';
      if (resData.status === 'verified' && lessThan24Hours) {
        type = StepType.NEXT;
        body = 'Now';
      } else if (['pending', 'open', 'verified'].includes(resData.status)) {
        type = StepType.UPCOMING;
        body = format(
          subDays(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      } else {
        type = StepType.COMPLETED;
      }

      return {
        type: type as StepType,
        title: `Authorize your $${resData.holdAmount / 100} security deposit`,
        body: body,
      };
    };

    const renderCheckIn = (): TripStep => {
      let type = '';
      let body = '';

      if (resData.isCheckinComplete) {
        type = StepType.COMPLETED;
      } else if (
        ['confirmed', 'rental'].includes(resData.status) &&
        !resData.isCheckinComplete
      ) {
        type = StepType.NEXT;
        body = format(
          subHours(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      } else {
        type = StepType.UPCOMING;
        body = format(
          subHours(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      }

      return {
        type: type as StepType,
        title: 'Check-In',
        body: body,
      };
    };

    const renderPickup = (): TripStep => {
      let type = '';
      let body = '';

      const now = new Date();
      const startTimeDate = toZonedTime(
        resData.startTime,
        resData.pickupLocation.timezone
      );

      if (resData.isCheckinComplete && now > new Date(resData.startTime)) {
        type = StepType.COMPLETED;
      } else if (resData.isCheckinComplete) {
        type = StepType.NEXT;
        body = format(startTimeDate, 'MMM dd - hh:mm a');
      } else {
        type = StepType.UPCOMING;
        body = format(startTimeDate, 'MMM dd - hh:mm a');
      }

      return {
        type: type as StepType,
        title: resData.customPickupLocation ? 'Delivery' : 'Pickup',
        body: body,
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customPickupLocation || resData.pickupLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customPickupLocation || resData.pickupLocation.address}
            </Text>
          </Pressable>
        ),
      };
    };

    const renderDropoff = (): TripStep => {
      let type = '';
      let body = '';

      const now = new Date();
      const endDatetime = toZonedTime(
        resData.endTime,
        resData.returnLocation.timezone
      );

      if (['cleaning', 'billable', 'completed'].includes(resData.status)) {
        type = StepType.COMPLETED;
      } else if (
        resData.status === 'rental' &&
        now >= new Date(resData.endTime)
      ) {
        type = StepType.NEXT;
        body = 'Now';
      } else if (
        resData.isCheckinComplete &&
        now > new Date(resData.startTime) &&
        now < new Date(resData.endTime)
      ) {
        type = StepType.NEXT;
        body = format(endDatetime, 'MMM dd - hh:mm a');
      } else {
        type = StepType.UPCOMING;
        body = format(endDatetime, 'MMM dd - hh:mm a');
      }

      return {
        type: type as StepType,
        title: 'Dropoff',
        body: body,
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customReturnLocation || resData.returnLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customReturnLocation || resData.returnLocation.address}
            </Text>
          </Pressable>
        ),
      };
    };

    const renderFinalReceipt = (): TripStep => {
      let type = '';
      let body = '';

      const endDatetime = toZonedTime(
        resData.endTime,
        resData.returnLocation.timezone
      );

      if (['cleaning', 'billable'].includes(resData.status)) {
        type = StepType.NEXT;
        body = format(addDays(endDatetime, 2), 'MMM dd');
      } else if (resData.status === 'completed') {
        type = StepType.COMPLETED;
      } else {
        type = StepType.UPCOMING;
        body = format(addDays(endDatetime, 2), 'MMM dd');
      }

      return {
        type: type as StepType,
        title: 'Get your final receipt',
        body: body,
      };
    };

    const steps: TripStep[] = [
      {
        type: StepType.COMPLETED,
        title: 'Book Your Trip',
        body: '',
      },
      {
        type: ['pending', 'open'].includes(resData.status)
          ? StepType.NEXT
          : StepType.COMPLETED,
        title:
          "Confirm your insurance, driver's license and payment information.",
        body: ['pending', 'open'].includes(resData.status) ? 'Now' : '',
      },
      ...(resData.holdAmount > 0 ? [renderDepositStep()] : []),
      renderCheckIn(),
      renderPickup(),
      renderDropoff(),
      renderFinalReceipt(),
    ];

    return steps;
  }, [resData]);

  const handleAuthorizeDeposit = async () => {
    setShowPaymentModal(true);
  };

  const handleHoldPayment = async () => {
    if (!stripe || !elements) return;

    setIsHandlingPaymentPending(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log('submit error', submitError);
      setIsHandlingPaymentPending(false);
      setIsHandlingPaymentError(true);
      showErrorMessage('Payment failed. Please try again.');
      return;
    }

    let errorDuringBooking = null;
    if (paymentMethodId !== '-1') {
      const { error } = await stripe.confirmCardPayment(stripeSecret, {
        payment_method: paymentMethodId as string,
      });

      errorDuringBooking = error;
    } else if (paymentMethodId === '-1') {
      console.log('elements', elements);
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      errorDuringBooking = error;
    }

    if (errorDuringBooking) {
      console.log('error', errorDuringBooking);
      setIsHandlingPaymentPending(false);
      setIsHandlingPaymentError(true);
      showErrorMessage('Payment failed. Please try again.');
      return;
    }

    setShowPaymentModal(false);

    const latest = await pollForTripConfirmed();

    setIsHandlingPaymentPending(false);

    if (latest && isTripConfirmed(latest)) {
      setIsHandlingPaymentSuccess(true);
    } else {
      setIsHandlingPaymentError(true);
      showErrorMessage('Payment failed. Please try again.');
    }
  };

  const dialog = React.useMemo(() => {
    if (!resData) return null;

    if (additionalDriver) {
      return null;
    }

    if (resData.customer.tripsInCollection.length > 0 && !resData.lockedOutAt) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text="Outstanding balance"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body={
            resData.customer.tripsInCollection.find((t) => t.id === resData.id)
              ? `You have an outstanding balance of $${formatNumber(
                  resData.outstandingBalance
                )}. Please settle the amount or contact support for assistance. `
              : 'You have an outstanding balance from a previous rental. You will need to settle the outstanding amount to continue renting. '
          }
          button={
            <Button
              text={
                resData.customer.tripsInCollection.find(
                  (t) => t.id === resData.id
                )
                  ? 'Pay Balance'
                  : 'See Details'
              }
              type="primarySmall"
              onPress={() => {
                router.push(
                  `/account/trip-history/${resData.customer.tripsInCollection[0].id}`
                );
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              }
              style={{ width: 150 }}
            />
          }
        />
      );
    }

    if (resData.status === 'completed') {
      return null;
    }

    if (resData.status === 'billable') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Receipt in progress"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Your final receipt is being prepared and may change. Please allow up to 48 hours for completion."
        />
      );
    }

    if (resData.status === 'cleaning') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Receipt in progress"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Your final receipt is being prepared and may change. Please allow up to 48 hours for completion."
        />
      );
    }

    if (resData.status === 'rental' && !resData.isCheckinComplete) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Complete check-in to start your trip"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Complete your Check In to unlock your car."
        />
      );
    }

    if (
      resData.status === 'rental' &&
      isAfter(new Date(), subHours(new Date(resData.endTime), 3))
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Complete Drop-off after your trip."
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="End your trip by uploading photos of the car and completing the drop-off instructions."
        />
      );
    }

    if (
      resData.status === 'rental' &&
      resData.isCheckinComplete &&
      resData.preTripCarImages.length > 0
    ) {
      return null;
    }

    if (
      resData.status === 'rental' &&
      resData.isCheckinComplete &&
      resData.preTripCarImages.length === 0
    ) {
      return null;
    }

    if (resData.status === 'confirmed') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Check-in will be open 1 hour before your trip"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Come back 1 hour before your trip starts to complete check-in and receive driving instructions / PIN for the car."
        />
      );
    }

    if (
      resData.status === 'verified' &&
      differenceInHours(new Date(resData.startTime), new Date()) < 24 &&
      resData.payments.find((p) => p.isHold)
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Almost Done"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Our team is briefly wrapping up some final details, but you're all set for now!"
        />
      );
    }

    if (
      resData.status === 'verified' &&
      resData.holdAmount > 0 &&
      differenceInHours(new Date(resData.startTime), new Date()) < 24
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text="Authorize Deposit"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body={`Your $${formatNumber(
            resData.holdAmount / 100
          )} rental deposit authorization is in progress. Make sure to complete this to access your car.`}
          button={
            <Button
              text="Authorize Deposit"
              type="primarySmall"
              onPress={handleAuthorizeDeposit}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.button.primary.default.text}
                  width={12}
                  height={12}
                />
              }
              style={{ width: 200 }}
            />
          }
        />
      );
    }

    if (resData.status === 'verified') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Check-in will be open 1 hour before your trip"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Come back 1 hour before your trip starts to complete check-in and receive driving instructions / PIN for the car."
        />
      );
    }

    if (
      resData.customer.driversLicenseStatus === 'rejected' ||
      (resData.customer.insuranceStatus === 'rejected' &&
        resData.customer.insuranceNeeded)
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text="Verification failed"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="We couldn't verify your information. Please try again."
        />
      );
    }

    if (resData.customer.insuranceStatus === 'pending') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Verification processing"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Great news! Your rental is booked. We are currently verifying your details."
        />
      );
    }

    if (
      ((resData.customer.insuranceNeeded &&
        resData.customer.insuranceStatus === 'approved') ||
        !resData.customer.insuranceNeeded) &&
      resData.customer.driversLicenseStatus === 'approved' &&
      (!resData.customer.paymentVerificationStatus ||
        resData.customer.paymentVerificationStatus === 'approved') &&
      resData.customer.backgroundCheckStatus !== 'approved'
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.PENDING}
              icon={<Dot />}
              text="Under review"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="Your profile is being verified and will be completed shortly."
        />
      );
    }

    return (
      <Dialog
        header={
          <Chip
            type={ChipType.WARNING}
            icon={<Dot />}
            text="Verification needed"
            iconWidth={8}
            iconHeight={8}
          />
        }
        body="Great news! Your rental is booked. Before you hit the road, just make sure you have your details verified. "
      />
    );
  }, [resData]);

  const checklistItems = React.useMemo(() => {
    if (!resData) return [];

    if (additionalDriver && !additionalDriverVerified) return [];

    const subtext = (status: any) => {
      switch (status) {
        case 'pending':
          return (
            <Text type="caption1">Your verification is under review.</Text>
          );
        case 'rejected':
          return (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Your verification was rejected. Try again or contact customer
              support.
            </Text>
          );
        default:
          return '';
      }
    };

    let items: any[] = [];

    if (resData.status === 'cleaning' && resData.customerMarkedFinishedAt) {
      items = [
        {
          status: 'upload',
          text: 'Take Post-Trip Photos',
          subtext: (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended
            </Text>
          ),
          visible: true,
          buttonText: 'Take Photos',
          onPress: () => {
            router.push(`/trips/${resData.id}/upload-photos`);
          },
        },
      ];
    }

    if (resData.status === 'cleaning' && !resData.customerMarkedFinishedAt) {
      items = [
        {
          status: 'upload',
          text: 'Drop-off instructions',
          visible: true,
          buttonText: 'Drop-off',
          onPress: () => {
            router.push(`/trips/${resData.id}/drop-off`);
          },
        },
        {
          status: 'upload',
          text: 'Take Post-Trip Photos',
          subtext: (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended
            </Text>
          ),
          visible: true,
          buttonText: 'Take Photos',
          onPress: () => {
            router.push(`/trips/${resData.id}/upload-photos`);
          },
        },
      ];
    }

    if (
      resData.status === 'rental' &&
      isAfter(new Date(), subHours(new Date(resData.endTime), 12))
    ) {
      items = [
        {
          status: 'upload',
          text: 'Drop-off instructions',
          visible: true,
          buttonText: 'Drop-off',
          onPress: () => {
            router.push(`/trips/${resData.id}/drop-off`);
          },
        },
      ];
    }

    if (
      resData.status === 'rental' &&
      isAfter(new Date(), subHours(new Date(resData.endTime), 3))
    ) {
      items = [
        {
          status: 'upload',
          text: 'Drop-off instructions',
          visible: true,
          buttonText: 'Drop-off',
          onPress: () => {
            router.push(`/trips/${resData.id}/drop-off`);
          },
        },
        {
          status: 'upload',
          text: 'Take Post-Trip Photos',
          subtext: (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended
            </Text>
          ),
          visible: true,
          buttonText: 'Take Photos',
          onPress: () => {
            router.push(`/trips/${resData.id}/upload-photos`);
          },
        },
      ];
    }

    if (
      resData.status === 'rental' &&
      resData.isCheckinComplete &&
      // resData.carImages.length === 0 &&
      isAfter(new Date(), subHours(new Date(resData.startTime), 1)) &&
      isBefore(new Date(), addHours(new Date(resData.startTime), 6))
    ) {
      items = [
        {
          text: 'Pickup Instructions',
          subtext: '',
          visible: true,
          buttonText: 'Locate',
          onPress: () => {
            router.push(`/trips/${resData.id}/car-details`);
          },
        },
        {
          status: 'upload',
          text: 'Take Pre-Trip Photos',
          subtext: (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended
            </Text>
          ),
          visible: true,
          buttonText: 'Take Photos',
          onPress: () => {
            router.push(`/trips/${resData.id}/upload-photos`);
          },
        },
      ];
    }

    if (resData.status === 'rental' && !resData.isCheckinComplete) {
      items = [
        {
          text: 'Pickup Instructions',
          subtext: '',
          visible: true,
          buttonText: 'Locate',
          onPress: () => {
            router.push(`/trips/${resData.id}/car-details`);
          },
        },
        {
          text: 'Take Pre-Trip Photos',
          subtext: (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Recommended
            </Text>
          ),
          visible: true,
          buttonText: 'Take Photos',
          onPress: () => {
            router.push(`/trips/${resData.id}/upload-photos`);
          },
        },
      ];
    }

    if (resData.status === 'open' && !additionalDriver) {
      items = [
        {
          status: resData.customer.driversLicenseStatus,
          text: "Driver's License",
          subtext: subtext(resData.customer.driversLicenseStatus),
          visible: true,
          onPress: () => {
            router.push(`/account/verify-license?trip_id=${resData.id}`);
          },
        },
        {
          status: resData.customer.insuranceStatus,
          text: 'Insurance',
          subtext: subtext(resData.customer.insuranceStatus),
          visible: resData.customer.insuranceNeeded,
          onPress: () => {
            router.push(`/account/verify-insurance?trip_id=${resData.id}`);
          },
        },
        {
          status: resData.customer.paymentVerificationStatus,
          text: 'Payment verification',
          subtext: subtext(resData.customer.paymentVerificationStatus),
          visible: !!resData.customer.paymentVerificationStatus,
          onPress: () => {
            router.push(`/account/verify-payment-method?trip_id=${resData.id}`);
          },
        },
      ];
    }

    return items;
  }, [resData, additionalDriver, additionalDriverVerified, router, theme]);

  const renderBody = () => {
    if (
      resData.status === 'completed' &&
      resData.customer.tripsInCollection.length === 0
    ) {
      const firstName =
        resData.customer.firstName ?? resData.customer.firstname;

      return (
        <View style={{ gap: 16 }}>
          <View style={{ gap: 4 }}>
            <Text type="title2">
              {`Hope You Enjoyed Your Trip, ${firstName}`}!
            </Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              You can find your receipt and trip details here.
            </Text>
          </View>

          <View
            style={{
              height: 1,
              width: '100%',
              backgroundColor: theme.colors.card.default.borderColor,
            }}
          />

          <View style={{ gap: 24 }}>
            <Pressable
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onPress={() => {
                router.push(`/account/trip-history/${id}`);
              }}
            >
              <View>
                <Text type="bodyBold">Receipt</Text>

                <Text type="caption1">
                  View your trip's charges and breakdown.
                </Text>
              </View>

              <View style={{ paddingRight: 16 }}>
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              </View>
            </Pressable>

            <Pressable
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onPress={() => {
                router.push(`/trips/${id}/rental-agreement`);
              }}
            >
              <View>
                <Text type="bodyBold">Rental Agreement</Text>

                <Text type="caption1">Terms & Conditions</Text>
              </View>

              <View style={{ paddingRight: 16 }}>
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              </View>
            </Pressable>
          </View>

          <View
            style={{
              height: 1,
              width: '100%',
              backgroundColor: theme.colors.card.default.borderColor,
            }}
          />

          <Card>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text type="title2">Trip Photos</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Access your pre and post trip photos here
                </Text>
              </View>
            </View>

            <View style={{ padding: 16, gap: 16 }}>
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <Pressable onPress={() => setPhotosModeTab('pre')}>
                  <Text
                    type="bodyRegular"
                    style={{
                      textDecorationLine:
                        photosModeTab === 'pre' ? 'underline' : 'none',
                    }}
                  >
                    Pre-trip Photos
                  </Text>
                </Pressable>

                <Pressable onPress={() => setPhotosModeTab('post')}>
                  <Text
                    type="bodyRegular"
                    style={{
                      textDecorationLine:
                        photosModeTab === 'post' ? 'underline' : 'none',
                    }}
                  >
                    Post-trip Photos
                  </Text>
                </Pressable>
              </View>

              <View>
                {photosModeTab === 'pre' ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 4,
                    }}
                  >
                    {resData?.preTripCarImages?.map((image, index) => {
                      return (
                        <Pressable
                          style={{
                            backgroundColor: theme.colors.background_3,
                            padding: 8,
                          }}
                          key={`pre-image-${index}`}
                          onPress={() => {
                            setActiveImage(image);
                          }}
                        >
                          <Image
                            source={{ uri: image.photo }}
                            style={{ width: 60, height: 60 }}
                            contentFit="contain"
                          />
                        </Pressable>
                      );
                    })}
                  </View>
                ) : (
                  <View
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 4,
                    }}
                  >
                    {resData?.postTripCarImages?.map((image, index) => {
                      return (
                        <Pressable
                          style={{
                            backgroundColor: theme.colors.background_3,
                            padding: 8,
                          }}
                          key={`post-image-${index}`}
                          onPress={() => {
                            setActiveImage(image);
                          }}
                        >
                          <Image
                            source={{ uri: image.photo }}
                            style={{ width: 60, height: 60 }}
                            contentFit="contain"
                          />
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
            </View>
          </Card>

          {resData.incidentals?.length > 0 ? (
            <Card>
              <View
                style={{
                  borderBottomColor: theme.colors.card.default.borderColor,
                  borderBottomWidth: 1,
                }}
              >
                <View style={{ padding: 16 }}>
                  <Text type="title2">Incidentals</Text>

                  <Text type="bodyRegular">
                    Access your incidental documents here
                  </Text>
                </View>
              </View>

              <View style={{ padding: 16, gap: 16 }}>
                {resData.incidentals.map((incidental, index) => {
                  return (
                    <Pressable
                      key={`incidental-${index}`}
                      onPress={() => {
                        router.push(
                          `/account/trip-history/${id}/incidentals/${incidental.id}`
                        );
                      }}
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text>
                        {`${incidental.category} ${format(
                          incidental.offenseDate,
                          'dd MM yyyy'
                        )}`}
                      </Text>

                      <Icon
                        type="naked"
                        icon={<ArrowRight />}
                        fillColor={theme.colors.icon.color}
                        width={12}
                        height={12}
                      />
                    </Pressable>
                  );
                })}
              </View>
            </Card>
          ) : null}

          <Review id={resData.id} />

          <Card style={{ gap: 16 }}>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text tx="referrals.title" type="title2" />

                <Text type="bodyRegular" style={{ color: GREY_40 }}>
                  Invite a friend and earn $35 in Eon credit once they complete
                  their first trip. Plus, they get $35 off their first booking!
                </Text>
              </View>
            </View>

            <View style={{ gap: 16, padding: 16 }}>
              <Input
                label="Referral Link"
                value={`${Env.BASE_URL}?ref=${resData.customer.referralCode}`}
                disabled
              />

              <Button
                type="primary"
                tx="referrals.share"
                onPress={handleShare}
              />
            </View>
          </Card>

          <ContactSupport />
        </View>
      );
    }

    return (
      <View style={{ gap: 24 }}>
        {dialog}

        {resData?.lockedOutAt && !additionalDriver ? (
          <View>
            <Alert
              type={AlertType.ERROR}
              title="Car locked"
              body={`You have an outstanding balance of $${formatNumber(
                resData?.outstandingBalance ?? 0
              )}. Pay now to restore access to your car.`}
              icon={<Warning />}
              buttonText="Pay balance"
              onPress={() => {
                router.replace(`/account/trip-history/${resData?.id}`);
              }}
            />
          </View>
        ) : null}

        {/* {resData?.controlPanelState === 'disabled' ? (
          <Pressable
            style={{
              backgroundColor: theme.colors.card.disabled.backgroundColor,
              borderColor: theme.colors.card.disabled.borderColor,
              borderWidth: 1,
              borderRadius: 16,
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 16,
              paddingBottom: 16,
              gap: 8,
            }}
            disabled
          >
            <Icon
              type="dressed"
              icon={<ControlPanel />}
              width={18}
              height={18}
              fillColor={theme.colors.text.secondary50}
            />

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary50 }}
            >
              Check-in first to unlock control panel
            </Text>
          </Pressable>
        ) : null}

        {resData?.controlPanelState === 'enabled' ? (
          <MenuItem
            leftIcon={<ControlPanel />}
            leftIconFillColor={theme.colors.chip.brand.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="tripDetails.controlPanelTitle"
            onPress={() => {
              router.navigate(`/trips/${id}/car-control-panel`);
            }}
            style={{
              width: '100%',
              backgroundColor: 'rgba(225, 92, 247, 0.2)',
              borderColor: 'rgba(225, 92, 247, 0.2)',
              borderWidth: 1,
            }}
          />
        ) : null} */}

        {resData.status === 'confirmed' ||
        (resData.status === 'rental' && !resData?.isCheckinComplete) ? (
          <View
            style={{
              borderRadius: 24,
              borderWidth: 1,
              borderColor: theme.colors.chip.pending.border,
              backgroundColor: theme.colors.chip.pending.backgroundColor,
              padding: 16,
            }}
          >
            <View style={{ gap: 8 }}>
              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                <Icon
                  type="naked"
                  icon={<Docs />}
                  fillColor={theme.colors.pending}
                  width={24}
                  height={24}
                />

                <Text type="title2">What to Bring</Text>
              </View>

              <View style={{ paddingLeft: 8 }}>
                <View
                  style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
                >
                  <Text>{'\u2022'}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Valid driver's license (physical copy required)
                  </Text>
                </View>

                <View
                  style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
                >
                  <Text>{'\u2022'}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Your phone with the Eon app installed
                  </Text>
                </View>
              </View>
            </View>
          </View>
        ) : null}

        {checklistItems.length > 0 ? (
          <Card>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text type="title2">Your Trip Checklist</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {isAfter(new Date(), subHours(new Date(resData.endTime), 12))
                    ? 'Complete these steps before your trip ends'
                    : 'Complete these steps before your trip starts'}
                </Text>
              </View>
            </View>

            <View style={{ padding: 16, gap: 16 }}>
              {checklistItems
                .filter((item) => item.visible)
                .map((item, index) => {
                  let icon;

                  switch (item.status) {
                    case 'upload':
                      icon = (
                        <Icon
                          type="naked"
                          icon={<Hourglass />}
                          fillColor={theme.colors.icon.color}
                          width={12}
                          height={12}
                        />
                      );
                      break;
                    case 'pending':
                      icon = (
                        <Icon
                          type="naked"
                          icon={<Hourglass />}
                          fillColor={theme.colors.icon.color}
                          width={12}
                          height={12}
                        />
                      );
                      break;
                    case 'approved':
                      icon = (
                        <Icon
                          type="naked"
                          icon={<Checkmark />}
                          stroke={theme.colors.success}
                          fillColor={theme.colors.success}
                          width={12}
                          height={12}
                        />
                      );
                      break;
                    case 'rejected':
                      icon = (
                        <Icon
                          type="naked"
                          icon={<Cancel />}
                          stroke={theme.colors.error}
                          fillColor={theme.colors.error}
                          width={12}
                          height={12}
                        />
                      );
                      break;
                  }

                  return (
                    <ListItemStatus
                      key={`checklist-item-${index}`}
                      icon={icon}
                      text={item.text}
                      subtext={item.subtext}
                      buttonText={
                        item.buttonText ||
                        (['upload', 'rejected'].includes(item.status)
                          ? 'Verify'
                          : '')
                      }
                      onPress={() => {
                        item.onPress();
                      }}
                    />
                  );
                })}
            </View>
          </Card>
        ) : null}

        {!resData?.car.isOffline &&
        resData?.status === 'rental' &&
        resData?.isCheckinComplete &&
        (!additionalDriver || additionalDriverVerified) ? (
          <Card>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <View style={{ padding: 16, gap: 8 }}>
                <Text type="title2">Car Access</Text>

                {!isDesktop() && resData?.car.isStandardFleetConnected ? (
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Stay near the car with the ChargeKey app open to unlock and
                    start the car.
                  </Text>
                ) : null}
              </View>
            </View>

            {isDesktop() ? (
              <View
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                  padding: 16,
                }}
              >
                <Text type="bodyRegular">
                  Download the Eon App to get access to your car
                </Text>

                <Image
                  style={{
                    width: 100,
                    height: 100,
                  }}
                  source={
                    UnistylesRuntime.themeName === 'dark'
                      ? require('@/../public/images/qr-onelink-dark.png')
                      : require('@/../public/images/qr-onelink-light.png')
                  }
                  contentFit="cover"
                />
              </View>
            ) : (
              <View>
                <View style={{ padding: 16, gap: 24 }}>
                  {resData?.car.isStandardFleetConnected ? (
                    <View>
                      <Image
                        style={{
                          width: '100%',
                          height: 190,
                        }}
                        source={
                          UnistylesRuntime.themeName === 'dark'
                            ? require('@/../public/images/trip-details/car-signal-dark.png')
                            : require('@/../public/images/trip-details/car-signal-light.png')
                        }
                        contentFit="cover"
                      />
                    </View>
                  ) : null}

                  {resData?.paidGlovebox ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <Card
                        style={{
                          flex: 1,
                          backgroundColor:
                            theme.colors.card.hover.backgroundColor,
                          paddingLeft: 16,
                          paddingRight: 16,
                          paddingTop: 8,
                          paddingBottom: 8,
                        }}
                      >
                        <View
                          style={{
                            alignItems: 'center',
                            paddingTop: 8,
                            paddingBottom: 8,
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Glovebox PIN
                          </Text>

                          <Text type="title1">{resData?.gloveboxPin}</Text>
                        </View>
                      </Card>
                    </View>
                  ) : null}

                  {resData?.car.isStandardFleetConnected ? (
                    <View style={{ gap: 16 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <View style={{ gap: 4, width: '60%' }}>
                          <Text type="bodyRegular">Digital Car Key</Text>

                          <Text
                            type="caption1"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Required to unlock and start your car.
                          </Text>
                        </View>

                        <Button
                          text="Open"
                          type="primaryExtraSmall"
                          onPress={async () => {
                            const sfKeyLink =
                              Platform.OS === 'web'
                                ? resData.sfCarWebKeyLink
                                : resData.sfCarKeyLink;

                            await Linking.openURL(sfKeyLink);
                          }}
                          style={{ height: 32 }}
                        />
                      </View>

                      {!additionalDriver && !resData?.paidGlovebox ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <View style={{ gap: 4, width: '65%' }}>
                            <Text type="bodyRegular">Physical Car Key</Text>

                            <Text
                              type="caption1"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              Need to valet? Unlock the glovebox for the
                              physical key. Additional deposit of $250 is
                              required for security.
                            </Text>
                          </View>

                          <Button
                            text={Platform.OS === 'web' ? 'Open App' : 'Unlock'}
                            type="secondaryExtraSmall"
                            rightIcon={
                              <Icon
                                type="naked"
                                icon={<Lock />}
                                fillColor={theme.colors.icon.color}
                                width={10}
                                height={10}
                              />
                            }
                            onPress={async () => {
                              if (Platform.OS === 'web') {
                                await Linking.openURL(
                                  'https://appeon.onelink.me/nQbG/5epwb56h'
                                );
                              }
                            }}
                            style={{ height: 32 }}
                          />
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                </View>
              </View>
            )}
          </Card>
        ) : null}

        {resData?.status === 'rental' &&
        !resData?.isCheckinComplete &&
        (!additionalDriver || additionalDriverVerified) ? (
          <Card>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <View
                style={{
                  padding: 16,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <View style={{ gap: 8, width: '65%' }}>
                  <Text type="title2">Car Access</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Check in to unlock access to your car
                  </Text>
                </View>

                <View>
                  <Button
                    text="Check in"
                    type="primaryExtraSmall"
                    rightIcon={
                      <Icon
                        type="naked"
                        icon={<ArrowRight />}
                        fillColor={theme.colors.button.primary.default.text}
                        width={10}
                        height={10}
                      />
                    }
                    onPress={() => {
                      router.push(`/trips/${resData.id}/checkin`);
                    }}
                    style={{ width: 100 }}
                  />
                </View>
              </View>
            </View>

            <View style={{ padding: 16 }}>
              <Image
                style={{
                  width: '100%',
                  height: 100,
                }}
                source={
                  UnistylesRuntime.themeName === 'dark'
                    ? require('@/../public/images/trip-details/locked-features-dark.png')
                    : require('@/../public/images/trip-details/locked-features-light.png')
                }
                contentFit="cover"
              />
            </View>
          </Card>
        ) : null}

        {!additionalDriver &&
        resData?.mileagePlan.unlimited &&
        resData?.outstandingBalance > 0 ? (
          <Card style={{ padding: 16, gap: 16 }}>
            <View>
              <Text type="title2">Amount Owed</Text>
            </View>

            <Card
              style={{
                backgroundColor: theme.colors.card.hover.backgroundColor,
                padding: 16,
                gap: 8,
              }}
            >
              <View>
                <Text type="bodyRegular">Amount due today</Text>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Including charging and tolls
                </Text>
              </View>

              <View>
                <Text type="title2">
                  ${formatNumber(resData?.outstandingBalance ?? 0)}
                </Text>
              </View>
            </Card>

            <Button
              text="Pay Balance"
              type="primarySmall"
              onPress={() => {
                router.replace(`/account/trip-history/${resData?.id}`);
              }}
            />
          </Card>
        ) : null}

        {resData?.status === 'rental' && resData?.isCheckinComplete ? (
          <Card>
            <Pressable
              style={{ padding: 16, gap: 8 }}
              onPress={() => {
                router.push(`/trips/${id}/car-details`);
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <View
                  style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
                >
                  <View
                    style={{
                      backgroundColor: theme.colors.icon.fill,
                      padding: 10,
                      borderRadius: 8,
                      width: 40,
                      height: 40,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Image
                      style={{
                        width: 26,
                        height: 26,
                      }}
                      source={getCarBrandLogo(resData.car.brandLogos)}
                      contentFit="cover"
                    />
                  </View>

                  <View>
                    <Text type="title2">{`${
                      resData.car.brandName
                    } ${snakeToTitleCase(resData.car.modelName)}`}</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {snakeToTitleCase(resData.car.modelType)}
                    </Text>
                  </View>
                </View>

                <View>
                  <Image
                    source={{
                      uri:
                        resData.car &&
                        getCarImages(resData.car.images).length > 0
                          ? getCarImages(resData.car.images, 'small')[0].url
                          : '',
                    }}
                    style={{
                      width: isDesktop() ? 150 : 120,
                      aspectRatio: 16 / 9,
                    }}
                    contentFit="cover"
                  />
                </View>

                <View>
                  <Icon
                    type="naked"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />
                </View>
              </View>

              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <View>
                  <Icon
                    type="dressed"
                    icon={<License />}
                    fillColor={theme.colors.icon.color}
                    width={25}
                    height={25}
                    style={{ width: 40, height: 40 }}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <Text type="bodyRegular">
                    {`License plate: ${snakeToTitleCase(
                      resData.car.licensePlateState
                    )} | ${resData.car.licensePlate}`}
                  </Text>
                </View>
              </View>

              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <View>
                  <Icon
                    type="dressed"
                    icon={<Call />}
                    fillColor={theme.colors.icon.color}
                    width={25}
                    height={25}
                    style={{ width: 40, height: 40 }}
                  />
                </View>

                <View style={{ flex: 1 }}>
                  <Text type="bodyRegular">
                    {`Agent's phone: ${resData?.car.ownerPhone}`}
                  </Text>
                </View>
              </View>
            </Pressable>
          </Card>
        ) : null}

        <Card>
          <View
            style={{
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <View style={{ padding: 16 }}>
              <Text tx="checkoutComplete.rentalTimeline" type="title2" />
            </View>
          </View>

          <View style={{ padding: 16 }}>
            <TripTimeline steps={timelineSteps} />
          </View>
        </Card>

        {resData.status !== 'rental' || !resData?.isCheckinComplete ? (
          <Card>
            <Pressable
              style={{ padding: 16, gap: 8 }}
              onPress={() => {
                router.push(`/trips/${id}/car-details`);
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <View
                  style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
                >
                  <View
                    style={{
                      backgroundColor: theme.colors.icon.fill,
                      padding: 10,
                      borderRadius: 8,
                      width: 40,
                      height: 40,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Image
                      style={{
                        width: 26,
                        height: 26,
                      }}
                      source={getCarBrandLogo(resData.car.brandLogos)}
                      contentFit="cover"
                    />
                  </View>

                  <View>
                    <Text type="title2">{`${
                      resData.car.brandName
                    } ${snakeToTitleCase(resData.car.modelName)}`}</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {snakeToTitleCase(resData.car.modelType)}
                    </Text>
                  </View>
                </View>

                <View>
                  <Image
                    source={{
                      uri:
                        resData.car &&
                        getCarImages(resData.car.images).length > 0
                          ? getCarImages(resData.car.images, 'small')[0].url
                          : '',
                    }}
                    style={{
                      width: isDesktop() ? 150 : 120,
                      aspectRatio: 16 / 9,
                    }}
                    contentFit="cover"
                  />
                </View>

                <View>
                  <Icon
                    type="naked"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />
                </View>
              </View>

              {['rental', 'cleaning', 'billable', 'completed'].includes(
                resData.status
              ) ? (
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View>
                    <Icon
                      type="dressed"
                      icon={<License />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                      style={{ width: 40, height: 40 }}
                    />
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text type="bodyRegular">
                      {`License plate: ${snakeToTitleCase(
                        resData.car.licensePlateState
                      )} | ${resData.car.licensePlate}`}
                    </Text>
                  </View>
                </View>
              ) : null}

              {['rental', 'cleaning'].includes(resData.status) ? (
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View>
                    <Icon
                      type="dressed"
                      icon={<Call />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                      style={{ width: 40, height: 40 }}
                    />
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text type="bodyRegular">
                      {`Agent's phone: ${resData?.car.ownerPhone}`}
                    </Text>
                  </View>
                </View>
              ) : null}
            </Pressable>
          </Card>
        ) : null}

        {!additionalDriver ? (
          <View style={{ gap: 24 }}>
            <AdditionalDriversCard
              drivers={resData.additionalDrivers}
              tripStatus={resData.status}
              onManage={() => {
                router.push(`/trips/${resData.id}/additional-drivers`);
              }}
            />

            <Text type="title2">Manage Your Trip</Text>

            <View style={{ gap: 16 }}>
              <Button
                text="Extend Trip"
                type="secondary"
                onPress={() => {
                  router.push(`/trips/${resData.id}/extension`);
                }}
                disabled={
                  resData.status !== 'rental' || !!resData?.subscription
                }
              />

              <Button
                text="Cancel Trip"
                type="ghost"
                onPress={() => {
                  if (resData?.subscription) {
                    setShowSubscriptionCancelTripModal(true);
                  } else {
                    setShowCancelTripModal(true);
                  }
                }}
                disabled={
                  resData ? isAfter(now, new Date(resData.startTime)) : true
                }
              />
            </View>
          </View>
        ) : null}

        <View
          style={{
            height: 1,
            width: '100%',
            backgroundColor: theme.colors.card.default.borderColor,
          }}
        />

        <View style={{ gap: 24 }}>
          {!additionalDriver ? (
            <>
              <Pressable
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onPress={() => {
                  router.push(`/account/trip-history/${id}`);
                }}
              >
                <View>
                  <Text type="bodyBold">Receipt</Text>

                  <Text type="caption1">
                    View your trip's charges and breakdown.
                  </Text>
                </View>

                <View style={{ paddingRight: 16 }}>
                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />
                </View>
              </Pressable>

              <Pressable
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onPress={() => {
                  router.push(`/trips/${id}/rental-agreement`);
                }}
              >
                <View>
                  <Text type="bodyBold">Rental Agreement</Text>

                  <Text type="caption1">Terms & Conditions</Text>
                </View>

                <View style={{ paddingRight: 16 }}>
                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />
                </View>
              </Pressable>
            </>
          ) : null}
          <Pressable
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            onPress={() => {
              router.push('/account/support-faq');
            }}
          >
            <View>
              <Text type="bodyBold">Contact support</Text>
            </View>

            <View style={{ paddingRight: 16 }}>
              <Icon
                type="naked"
                icon={<ArrowRight />}
                fillColor={theme.colors.icon.color}
                width={12}
                height={12}
              />
            </View>
          </Pressable>
        </View>
      </View>
    );
  };

  if (!showPaymentModal && isHandlingPaymentPending) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader
          title="Authorizing deposit"
          body="This could take a little bit..."
          style={{ width: 200, height: 200 }}
        />
      </View>
    );
  }

  if (isFetchingReservation || !resData) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={{ flexGrow: 1, gap: 32, alignItems: 'center' }}
      showsVerticalScrollIndicator={false}
    >
      {activeImage ? (
        <View style={styles.activeImageContainer}>
          {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  setActiveImage(null);
                }}
              />

              <Text type="largeTitle">Trip Summary</Text>
            </View>
          ) : null}

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {isDesktop() ? (
              <Pressable
                onPress={() => {
                  carouselRef.current?.prev();
                }}
              >
                <Icon
                  type="dressed"
                  icon={<ArrowLeft />}
                  fillColor={theme.colors.icon.color}
                />
              </Pressable>
            ) : null}

            <View>
              <Carousel
                ref={carouselRef}
                width={isDesktop() ? 500 : PAGE_WIDTH}
                height={isDesktop() ? 600 : PAGE_HEIGHT * 0.8}
                data={tripImagesToShow}
                defaultIndex={tripImagesToShow.findIndex(
                  (image) => image.photo === activeImage.photo
                )}
                loop={false}
                onProgressChange={progress}
                onSnapToItem={(index) => {
                  setCarouselActiveIndex(index);
                  setActiveImage(tripImagesToShow[index]);
                }}
                mode="parallax"
                modeConfig={{
                  parallaxScrollingScale: 0.95,
                  parallaxScrollingOffset: 10,
                }}
                renderItem={({ item }) => {
                  return (
                    <View
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                        gap: 4,
                      }}
                    >
                      <Image
                        source={{ uri: item.photo }}
                        style={{ width: '90%', height: '90%' }}
                        contentFit="contain"
                      />

                      <Text type="bodyRegular">
                        {item.photo.split('/').pop()}
                      </Text>
                    </View>
                  );
                }}
              />

              <Pagination.Basic
                progress={progress}
                data={tripImagesToShow}
                dotStyle={{
                  backgroundColor: theme.colors.paginationDots.empty,
                  borderRadius: 50,
                }}
                activeDotStyle={{
                  backgroundColor: theme.colors.paginationDots.filled,
                }}
                size={8}
                containerStyle={{ gap: 10, marginTop: 10 }}
                onPress={onPressPagination}
              />
            </View>

            {isDesktop() ? (
              <Pressable
                onPress={() => {
                  carouselRef.current?.next();
                }}
              >
                <Icon
                  type="dressed"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.icon.color}
                />
              </Pressable>
            ) : null}
          </View>

          {/* <Button
            text="Download"
            type="primary"
            onPress={() => {
              onDownloadFile(activeImage.photo);
            }}
            style={{ width: '95%' }}
          /> */}
        </View>
      ) : (
        <View style={styles.container}>
          {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.back();
                }}
              />

              <Text type="largeTitle">
                {resData.status === 'completed'
                  ? 'Trip Summary'
                  : 'Trip Details'}
              </Text>
            </View>
          ) : null}

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
            style={styles.scrollView}
          >
            <View style={{ flexGrow: 1, gap: 32 }}>{renderBody()}</View>
          </ScrollView>
        </View>
      )}

      <CardElement />

      <Footer />

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={showPaymentModal}
        onBackdropPress={() => {
          setShowPaymentModal(false);
        }}
        deviceWidth={PAGE_WIDTH}
      >
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            gap: 24,
            borderRadius: 16,
            padding: 16,
            justifyContent: 'center',
            width: '100%',
            maxWidth: 600,
            alignSelf: 'center',
          }}
        >
          <View
            style={{
              width: '100%',
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              justifyContent: 'center',
              paddingLeft: 16,
              marginRight: 16,
            }}
          >
            <Text type="title2">Authorize Deposit</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowPaymentModal(false);
              }}
              style={{
                position: 'absolute',
                right: 0,
              }}
            />
          </View>

          <PaymentElement
            onChange={(e) => {
              setIsFormValid(e.complete);
              setPaymentMethodId(e.value?.payment_method?.id || '-1');
            }}
            options={{
              business: { name: 'Eon' },
              layout: {
                type: 'accordion',
                radios: true,
                spacedAccordionItems: false,
              },
            }}
          />

          <Button
            type="primary"
            text={`Pay $${resData.holdAmount / 100}`}
            onPress={handleHoldPayment}
            disabled={!isFormValid}
          />
        </View>
      </Modal>

      <CancelTripModal
        visible={showCancelTripModal}
        onClose={() => setShowCancelTripModal(false)}
        reservation={resData}
      />

      <SubscriptionCancelTripModal
        visible={showSubscriptionCancelTripModal}
        onClose={() => setShowSubscriptionCancelTripModal(false)}
      />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 600,
    },
    alignSelf: 'center',
  },
  activeImageContainer: {
    width: {
      xs: '100%',
      md: 600,
    },
    gap: 32,
    backgroundColor: theme.colors.background_1,
  },
}));

export default StripeWrapper;
